import { ShiftManagementSelectableShiftsRowsDocument } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import ShiftsRow, {
  ShiftsRowType,
  TShiftsRow,
} from 'modules/shift_management/store/internal/models/ShiftsRow'

interface Result {
  fetch: (params: { endAt?: string; startAt?: string }) => Promise<ShiftsRow[]>
  loading: boolean
}

export const useSelectableShiftsRows = (): Result => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  // const currentPage = filters.statusId === SHIFT_CONFLICTS ? null : page
  const selectableEmployees = useSelectableEmployees(
    view.startAt,
    view.endAt,
    'read',
    filters.locationIds
  )
  const { loading: selectableEmployeesLoading, selectableEmployeeIds } = selectableEmployees

  const filteredEmployeeIds = useUnsafeMemo(() => {
    if (filters.terminated) {
      const requestedIds = filters.employeeIds?.map(Number)
      return requestedIds && requestedIds.length > 0 ? requestedIds : null
    }

    const requestedIds = filters.employeeIds?.map(Number)
    // If there are not filters (requestedIds is null), then show all the selectable employee ids.
    if (!requestedIds) return null

    // If there are requested ids, filter them to only include the selectable employee ids.
    return requestedIds.filter((id) => selectableEmployeeIds.includes(id))
  }, [filters.employeeIds, filters.terminated, selectableEmployeeIds])

  const [fetchEmployees, { loading: employeesLoading }] = useLazyQuery(
    ShiftManagementSelectableShiftsRowsDocument,
    { fetchPolicy: 'cache-first' }
  )

  const fetch = async (params: { endAt?: string; startAt?: string }): Promise<ShiftsRow[]> => {
    const startAt = params.startAt || view.startAt
    const endAt = params.endAt || view.endAt
    const { data: employeesData } = await fetchEmployees({
      variables: {
        contractOnDate: startAt,
        orderBy: localStorageOrderEmployeesBy,
        orderDir: localStorageOrderEmployeesDir,
        // page: currentPage,
        // perPage: MAX_ITEMS_PER_PAGE,
        customFilters: filters.customFilters,
        // - Accept employeeIds to refresh some isolated rows on update/delete
        employeeIds: filteredEmployeeIds,
        endAt,
        endAtISO: endAt,
        logicOperator: LOGIC_OPERATOR.AND,
        shiftIds: filters.shiftIds,
        withoutShiftIds: filters.withoutShiftIds,
        startAt,
        startAtISO: startAt,
        terminated: filters.terminated,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        legalEntityIds: filters.legalEntityIds,
      },
      fetchPolicy: 'cache-first',
    })

    return (
      employeesData?.shiftManagement?.employeesPermissions.map((shiftsRow: TShiftsRow) =>
        ShiftsRow.buildFromGraphQL(shiftsRow, ShiftsRowType.employee)
      ) || []
    )
  }

  return { fetch, loading: selectableEmployeesLoading || employeesLoading }
}
